/*
 * Copyright (c) 2023. American Express Company
 */

// theirs
import React from 'react';
import { Alert, FieldControl, Hint, Input, Label } from '@americanexpress/dls-react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';

// ours
import { PercentInputViewModel } from 'src/types/view-models/percent-input.view.model';
import styles from './PercentInput.module.scss';

export default function PercentInput({ id, label, hint, showFractions = false }: PercentInputViewModel): JSX.Element {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [field, meta, helpers]: [FieldInputProps<number>, FieldMetaProps<number>, FieldHelperProps<number>] =
        useField(id);
    const hasError: boolean = meta.touched && meta.error?.length > 0;
    const { setValue }: { setValue: (value: number) => void } = helpers;
    // DLS requires we associate Alert with Input in this particular way
    const alertId: string = `${id}Alert`;
    const describedBy: { describedBy?: string } = hasError ? { describedBy: alertId } : {};

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        let inputValue = e.target.value;
        const numbersOnly = /[^0-9.]/g;
        const twoDecimalPlaces = /(\d*\.?\d{0,2})/;
        inputValue = inputValue.replace(numbersOnly, '');
        const match = inputValue.match(twoDecimalPlaces);
        if (match) {
            inputValue = match[0];
        }
        setValue(Number(inputValue));
    };

    return (
        <FieldControl status={hasError ? 'error' : 'default'} data-cy={`${id}PercentInput`}>
            <Label htmlFor={id}>{label}</Label>
            {hint && <Hint htmlFor={id}>{hint}</Hint>}
            <div className={styles.percentInputContainer}>
                <Input
                    id={id}
                    type="number"
                    step="any"
                    value={meta.value === 0 ? '' : meta.value}
                    onChange={(e: any) => handleChange(e)}
                    showFractions={showFractions}
                    {...describedBy}
                />
                <span>%</span>
            </div>
            {hasError && <Alert id={alertId}>{meta.error}</Alert>}
        </FieldControl>
    );
}
